import { AppText } from "~/ui/app-text"
import { PhoneNumberInput, type PhoneNumberInputProps } from "~/ui/phone-number/phone-number-input"
import type { PhoneNumberValue } from "~/common/phone-number"
import type { ShareRoomMemberFragment } from "~/__generated__/graphql"
import { ArrowRight, LockSimple } from "phosphor-react"
import { isUsOrCaTimezone } from "~/common/platform"
import type { EventHandler, RefObject, MouseEvent } from "react"

const IS_US_OR_CA_TIMEZONE = isUsOrCaTimezone()

export type JoinBarPhoneProps = {
  mode: "phone"
  phoneNumberValue: PhoneNumberValue
  setPhoneNumberValue: PhoneNumberInputProps["onChange"]
  phoneInputRef: RefObject<HTMLInputElement>
}

export type JoinBarEmailProps = {
  mode: "email"
  emailValue: string
  setEmailValue: (value: string) => void
  emailInputRef: RefObject<HTMLInputElement>
}

export type JoinBarProps = {
  inviterData: ShareRoomMemberFragment | null
  handleJoinRoom: VoidFunction
  handleManualFocus: EventHandler<MouseEvent<HTMLDivElement>>
} & (JoinBarPhoneProps | JoinBarEmailProps)

const EmailInput = ({
  emailValue,
  setEmailValue,
  emailInputRef,
  handleJoinRoom,
}: JoinBarEmailProps & { handleJoinRoom: VoidFunction }) => (
  <AppText className="py-1">
    <input
      ref={emailInputRef}
      type="email"
      value={emailValue}
      onChange={(e) => setEmailValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") handleJoinRoom()
      }}
      placeholder="Enter your email"
      className="w-full border-none bg-transparent py-2 pl-5 pr-3 text-[18px] text-base-black placeholder-base-black/50 focus:outline-none"
    />
  </AppText>
)

const PhoneInput = ({
  phoneNumberValue,
  setPhoneNumberValue,
  phoneInputRef,
  handleJoinRoom,
}: JoinBarPhoneProps & { handleJoinRoom: VoidFunction }) => (
  <PhoneNumberInput
    ref={phoneInputRef}
    value={phoneNumberValue}
    onChange={setPhoneNumberValue}
    onSubmit={handleJoinRoom}
    inputClassName="max-w-[320px]"
  />
)

export const JoinBar = (props: JoinBarProps) => {
  const { inviterData, handleJoinRoom, handleManualFocus, mode } = props

  return (
    <>
      {inviterData && inviterData.displayOrUsername && (
        <div className="flex items-center justify-center gap-2 pt-3">
          <AppText variant="body3-medium" className="text-base-white md:text-15 md:tracking-n1-5">
            {inviterData.shortDisplayName} invited you to skip the waitlist
            <span>&nbsp;&nbsp;🎉</span>
          </AppText>
        </div>
      )}
      <div className="m-2 mx-auto flex max-w-[360px] items-center gap-[11px] rounded-full border border-base-black bg-base-white p-2">
        <div className="flex-1" onClick={mode === "phone" ? handleManualFocus : undefined}>
          {mode === "email" ? (
            <EmailInput {...(props as JoinBarEmailProps)} handleJoinRoom={handleJoinRoom} />
          ) : (
            <PhoneInput {...(props as JoinBarPhoneProps)} handleJoinRoom={handleJoinRoom} />
          )}
        </div>
        <button
          onClick={handleJoinRoom}
          className="flex h-[48px] w-[48px] items-center justify-center rounded-full border border-base-black bg-base-yellow"
        >
          <AppText variant="h2" className="uppercase text-base-black">
            <ArrowRight size={24} weight="bold" />
          </AppText>
        </button>
      </div>
      {mode === "phone" && (
        <div className="flex items-center justify-center pb-1">
          <LockSimple size={16} weight="fill" className="text-base-white opacity-50" />
          <AppText
            variant="body3-medium"
            className="text-base-white opacity-50 md:text-15 md:tracking-n1-5"
          >
            &nbsp;
            {`${IS_US_OR_CA_TIMEZONE ? "We'll text you a link. " : ""}Your # is kept private.`}
          </AppText>
        </div>
      )}
    </>
  )
}
